import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import { BranchPageHeader, BranchFilter, BranchBadge } from '../components/BranchUI';
import SearchBar, { matchesSearch } from '../components/SearchBar';
import { useAutoSave, AutoSaveIndicator } from '../hooks/useAutoSave';

const CRMPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [branchFilter, setBranchFilter] = useState(() => localStorage.getItem('realease_branch_filter') || 'all');
  const [search, setSearch] = useState('');
  const { user } = useAuth();

  useEffect(() => { localStorage.setItem('realease_branch_filter', branchFilter); }, [branchFilter]);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all' && !showArchived) params.set('status', filter);
      if (showArchived) params.set('archived', 'true');
      const q = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API}/crm/leads${q}`);
      setLeads(res.data);
      setSelectedIds(new Set());
    } catch {} finally { setLoading(false); }
  }, [filter, showArchived]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const list = (user?.cross_branch_access && branchFilter !== 'all')
      ? leads.filter(l => l.branch === branchFilter) : leads;
    if (selectedIds.size === list.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(list.map(l => l.lead_id)));
  };

  const handleBulk = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (action === 'delete' && !window.confirm(`Supprimer definitivement ${ids.length} prospect(s) ? Cette action est irreversible.`)) return;
    try {
      const r = await axios.post(`${API}/crm/leads/bulk`, { action, lead_ids: ids });
      fetchLeads();
      if (r.data?.count !== undefined) alert(`${r.data.count} prospect(s) ${action === 'delete' ? 'supprime(s)' : action === 'archive' ? 'archive(s)' : 'restaure(s)'}`);
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleArchive = async (leadId) => {
    try {
      await axios.patch(`${API}/crm/leads/${leadId}/archive`);
      fetchLeads();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Supprimer definitivement le prospect ${lead.first_name} ${lead.last_name} ? Cette action est irreversible.`)) return;
    try {
      await axios.delete(`${API}/crm/leads/${lead.lead_id}`);
      fetchLeads();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const statusLabel = { nouveau: 'Nouveau', a_qualifier: 'A qualifier', en_cours: 'En cours', accepte: 'Accepte', refuse: 'Refuse' };
  const canCreate = ['carla', 'admin', 'julie', 'ceo', 'amelie', 'collaborateur'].includes(user?.role);
  const canManage = ['carla', 'admin', 'ceo', 'julie', 'amelie', 'collaborateur'].includes(user?.role);

  // Filtrage client-side par branche + recherche (visible aux cross-branch users)
  const branchFiltered = (user?.cross_branch_access && branchFilter !== 'all')
    ? leads.filter(l => l.branch === branchFilter)
    : leads;
  const visibleLeads = !search.trim()
    ? branchFiltered
    : branchFiltered.filter(l => matchesSearch(l, search, [
        'first_name', 'last_name', 'email', 'phone', 'source', 'city', 'status', 'lead_id',
      ]));

  return (
    <div className="p-4 lg:p-8" data-testid="crm-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <BranchPageHeader user={user} baseTitle="CRM" />
          <h1 className="text-3xl font-bold text-gray-900">CRM / Pipeline</h1>
          <p className="text-gray-600 mt-1">{showArchived ? 'Prospects archives' : 'Gestion des leads et prospects'}</p>
        </div>
        {canCreate && !showArchived && (
          <button onClick={() => setShowModal(true)} className="realease-btn-primary flex items-center gap-2" data-testid="add-lead-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Nouveau prospect
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap items-center">
        {!showArchived && ['all', 'nouveau', 'a_qualifier', 'en_cours', 'accepte', 'refuse'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === s ? 'bg-[#008A8D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`filter-${s}`}>
            {s === 'all' ? 'Tous' : statusLabel[s]}
          </button>
        ))}
        {user?.cross_branch_access && (
          <div className="ml-3"><BranchFilter value={branchFilter} onChange={setBranchFilter} testidPrefix="crm" /></div>
        )}
        <div className="ml-auto">
          <button onClick={() => setShowArchived(!showArchived)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${showArchived ? 'bg-[#008A8D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="toggle-archived-leads">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
            {showArchived ? 'Prospects actifs' : 'Archives'}
          </button>
        </div>
      </div>

      {/* Barre de recherche */}
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Rechercher un prospect (nom, email, telephone, ville)..."
        resultsCount={visibleLeads.length}
        totalCount={branchFiltered.length}
        className="mb-4"
        testid="crm-search" />

      {canManage && selectedIds.size > 0 && (
        <div className="realease-card p-3 mb-4 flex items-center justify-between bg-[#008A8D]/5 border-[#008A8D]/30" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-[#008A8D]">{selectedIds.size} prospect{selectedIds.size > 1 ? 's' : ''} selectionne{selectedIds.size > 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">Annuler</button>
            <button onClick={() => handleBulk(showArchived ? 'unarchive' : 'archive')} className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1.5 rounded-md font-medium" data-testid="bulk-archive-leads">
              {showArchived ? 'Restaurer la selection' : 'Archiver la selection'}
            </button>
            <button onClick={() => handleBulk('delete')} className="text-sm bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md font-medium" data-testid="bulk-delete-leads">
              Supprimer la selection
            </button>
          </div>
        </div>
      )}

      <div className="realease-card overflow-hidden">
        {loading ? <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div>
        : visibleLeads.length === 0 ? <div className="p-8 text-center text-gray-500" data-testid="no-leads">{search.trim() ? <>Aucun prospect ne correspond a <b>"{search}"</b>. <button onClick={() => setSearch('')} className="text-[#008A8D] hover:underline ml-1">Effacer la recherche</button></> : (showArchived ? 'Aucun prospect archive.' : (branchFilter !== 'all' ? `Aucun prospect ${branchFilter === 'bretagne' ? 'Bretagne' : 'Nord'} pour le moment.` : 'Aucun prospect pour le moment.'))}</div>
        : (
          <table className="realease-table" data-testid="leads-table">
            <thead><tr>
              {canManage && <th className="w-8"><input type="checkbox" checked={selectedIds.size === visibleLeads.length && visibleLeads.length > 0} onChange={toggleSelectAll} className="w-4 h-4 accent-[#008A8D] cursor-pointer" data-testid="select-all-leads" /></th>}
              <th>Nom</th>{user?.cross_branch_access && <th>Branche</th>}<th>Email</th><th>Telephone</th><th>Source</th><th>Statut</th><th>Date</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {visibleLeads.map(lead => (
                <tr key={lead.lead_id} data-testid={`lead-row-${lead.lead_id}`} className={selectedIds.has(lead.lead_id) ? 'bg-[#008A8D]/5' : ''}>
                  {canManage && <td><input type="checkbox" checked={selectedIds.has(lead.lead_id)} onChange={() => toggleSelect(lead.lead_id)} className="w-4 h-4 accent-[#008A8D] cursor-pointer" data-testid={`select-lead-${lead.lead_id}`} /></td>}
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      {lead.global_score != null && (
                        <span title={`Score : ${Math.round(lead.global_score)}/100`}
                          className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${lead.global_score >= 70 ? 'bg-green-500' : lead.global_score >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          data-testid={`health-dot-${lead.lead_id}`}></span>
                      )}
                      <span>{lead.first_name} {lead.last_name}</span>
                      {lead.global_score != null && (
                        <span className="text-xs text-gray-500 font-normal">({Math.round(lead.global_score)}/100)</span>
                      )}
                    </div>
                  </td>
                  {user?.cross_branch_access && <td><BranchBadge branch={lead.branch} /></td>}
                  <td>{lead.email}</td>
                  <td>{lead.phone || '-'}</td>
                  <td>{lead.source || '-'}</td>
                  <td><span className={`status-badge status-${lead.status}`}>{statusLabel[lead.status]}</span></td>
                  <td className="text-gray-500">{new Date(lead.created_at).toLocaleDateString('fr-FR')}</td>
                  <td className="flex gap-2 items-center flex-wrap">
                    {!showArchived && (
                      <a href={`/qualification/${lead.lead_id}`} className="text-[#008A8D] hover:underline font-medium text-sm" data-testid={`qualify-btn-${lead.lead_id}`}>Qualifier</a>
                    )}
                    {!showArchived && (
                      <ClientDriveBtn lead={lead} onUpdated={fetchLeads} />
                    )}
                    {!showArchived && lead.status === 'accepte' && !lead.project_id && (
                      <CreateProjectButton leadId={lead.lead_id} onCreated={fetchLeads} />
                    )}
                    {!showArchived && lead.project_id && (
                      <>
                        <a href={`/projects/${lead.project_id}`} className="text-purple-600 hover:underline font-medium text-sm" data-testid={`view-project-${lead.lead_id}`}>Projet</a>
                        <InviteClientButton projectId={lead.project_id} clientEmail={lead.email} clientName={`${lead.first_name} ${lead.last_name}`} />
                      </>
                    )}
                    {canManage && (
                      <button onClick={() => handleArchive(lead.lead_id)} className="text-gray-500 hover:text-gray-800 text-sm" data-testid={`archive-lead-${lead.lead_id}`} title={showArchived ? 'Restaurer' : 'Archiver'}>
                        {showArchived ? 'Restaurer' : 'Archiver'}
                      </button>
                    )}
                    {canManage && (
                      <button onClick={() => handleDelete(lead)} className="text-red-500 hover:text-red-700 text-sm" data-testid={`delete-lead-${lead.lead_id}`} title="Supprimer">
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showModal && <AddLeadModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); fetchLeads(); }} />}
    </div>
  );
};

// Button to create project from accepted lead
const CreateProjectButton = ({ leadId, onCreated }) => {
  const [creating, setCreating] = useState(false);
  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/projects/from-lead/${leadId}`);
      onCreated();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    } finally { setCreating(false); }
  };
  return (
    <button onClick={handleCreate} disabled={creating} className="px-3 py-1 bg-[#008A8D] text-white text-xs rounded-lg font-medium hover:bg-[#006B6D] transition-colors disabled:opacity-50" data-testid={`create-project-btn-${leadId}`}>
      {creating ? '...' : 'Creer projet'}
    </button>
  );
};

// Bouton dossier Drive client (cree-le si manquant, sinon l'ouvre)
const ClientDriveBtn = ({ lead, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const cdf = lead.client_drive_folder;
  const url = cdf?.root_url;

  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer"
         className="text-blue-600 hover:underline text-sm flex items-center gap-1"
         title="Ouvrir le dossier client sur Google Drive"
         data-testid={`client-drive-${lead.lead_id}`}>
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/></svg>
        Drive
      </a>
    );
  }

  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/crm/leads/${lead.lead_id}/drive-folder`);
      const newUrl = r.data?.client_drive_folder?.root_url;
      if (newUrl) window.open(newUrl, '_blank');
      onUpdated && onUpdated();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur creation dossier Drive');
    } finally { setBusy(false); }
  };

  return (
    <button onClick={create} disabled={busy}
            className="text-blue-600 hover:underline text-sm flex items-center gap-1 disabled:opacity-50"
            title="Creer le dossier client sur Google Drive"
            data-testid={`create-client-drive-${lead.lead_id}`}>
      {busy ? '...' : '+ Drive'}
    </button>
  );
};

// Add Lead Modal
const AddLeadModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', source: '', notes: '' });

  // Auto-save : cree le lead des qu'un email valide est saisi, puis update en live (debounce 600ms)
  const { status, savedAt, error, recordId } = useAutoSave({
    state: form,
    canCreate: (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email || '') && (s.first_name?.trim() || s.last_name?.trim()),
    createFn: async (s) => {
      const r = await axios.post(`${API}/crm/leads`, s);
      return { id: r.data.lead_id, doc: r.data };
    },
    updateFn: async (id, s) => { await axios.put(`${API}/crm/leads/${id}`, s); },
    debounceMs: 600,
  });

  const handleClose = () => {
    // Si la fiche existe deja en base (recordId set), on rafraichit la liste
    if (recordId && onSuccess) onSuccess();
    else onClose();
  };

  const up = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="add-lead-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 animate-fade-in">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold">Nouveau prospect</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Sauvegarde automatique des qu'un prenom ou nom + email valide sont saisis. Plus besoin de cliquer.</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="realease-label">Prenom *</label><input type="text" value={form.first_name} onChange={e => up('first_name', e.target.value)} className="realease-input" data-testid="lead-first-name" /></div>
            <div><label className="realease-label">Nom *</label><input type="text" value={form.last_name} onChange={e => up('last_name', e.target.value)} className="realease-input" data-testid="lead-last-name" /></div>
          </div>
          <div><label className="realease-label">Email *</label><input type="email" value={form.email} onChange={e => up('email', e.target.value)} className="realease-input" data-testid="lead-email" /></div>
          <div><label className="realease-label">Telephone</label><input type="tel" value={form.phone} onChange={e => up('phone', e.target.value)} className="realease-input" data-testid="lead-phone" /></div>
          <div><label className="realease-label">Source</label>
            <select value={form.source} onChange={e => up('source', e.target.value)} className="realease-input" data-testid="lead-source">
              <option value="">Selectionner...</option><option value="website">Site web</option><option value="referral">Recommandation</option><option value="social">Reseaux sociaux</option><option value="ads">Publicite</option><option value="other">Autre</option>
            </select>
          </div>
          <div><label className="realease-label">Notes</label><textarea value={form.notes} onChange={e => up('notes', e.target.value)} className="realease-input h-24 resize-none" data-testid="lead-notes" /></div>
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <AutoSaveIndicator status={status} savedAt={savedAt} error={error} />
            <button type="button" onClick={handleClose}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      recordId ? 'bg-[#008A8D] text-white hover:bg-[#006D72]' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid="lead-close-btn">
              {recordId ? 'Fermer' : 'Annuler'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bouton inviter le client (utilise dans la ligne lead lorsque project_id existe)
const InviteClientButton = ({ leadId, projectId, clientEmail, clientName }) => {
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState(false);
  const handleInvite = async () => {
    setInviting(true);
    try {
      await axios.post(`${API}/invitations`, { email: clientEmail, client_name: clientName, project_id: projectId });
      setInvited(true);
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    finally { setInviting(false); }
  };
  if (invited) return <span className="text-green-600 text-xs font-medium">Invitation envoyee</span>;
  return (
    <button onClick={handleInvite} disabled={inviting} className="px-3 py-1 bg-purple-600 text-white text-xs rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50" data-testid={`invite-btn-${projectId}`}>
      {inviting ? '...' : 'Inviter client'}
    </button>
  );
};

export default CRMPage;
